/**
 * Owner: Masa Bayu
 * File: MarketplaceDetail.tsx
 * Date:Time: 2026-11-01 11:57
 * Description: Skrin QX eBook Product Detail — Hero + Related Buy Now via Hub
 *   purchaseViaQxpHub (R58); demo-cart bukan laluan beli related.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { getDemoCart } from '../../commerce/demo-cart-store';
import { hasCatalogOffer } from '../../commerce/catalog-offers';
import { LandingIcon } from './landing/LandingIcons';
import {
  isQxpHubCheckoutRedirect,
  loginQxreadHubAccount,
  purchaseViaQxpHub,
  type QxpHubPurchaseResult,
} from '../../publication/hub-sync';
import { useHubAuthState } from '../../publication/hub-auth';
import { getCatalogVersion, subscribeCatalog } from '../../publication/catalog-port';
import { MarketplaceNav } from './MarketplaceNav';
import { qxCategoryName } from '@qx/qxread-core';
import { MarketplaceFooter } from './MarketplaceFooter';
import { BookCard } from './BookCard';
import { MarketplaceDetailAuth } from './MarketplaceDetailAuth';
import { MarketplaceDetailHero } from './MarketplaceDetailHero';
import { listMarketplaceBooks } from './marketplace-projection';
import {
  getMarketplaceLocaleProfile,
  languageDisplayLabel,
  resolveMarketplaceLabels,
  safeIntlTag,
} from './marketplace-locale';
import { slugifyAuthorName } from '../../author/public/public-author-data';

export interface MarketplaceDetailProps {
  readonly publicationId: string;
  readonly onNavigate: (path: string) => void;
}

const RELATED_LIMIT = 4;
const SHARE_FEEDBACK_MS = 1600;

export function MarketplaceDetail({
  publicationId,
  onNavigate,
}: MarketplaceDetailProps): ReactElement {
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const hubAuth = useHubAuthState();
  // Buku dibaca semula pada setiap versi catalog — overlay hub (harga SSOT server)
  // tiba selepas sync permulaan dan skrin ini mesti mencerminkannya.
  const books = useMemo(() => listMarketplaceBooks(), [catalogVersion]);
  const [cartCount] = useState(() => getDemoCart().lines.length);
  const [shared, setShared] = useState(false);
  const shareTimer = useRef<number | null>(null);
  /** R58 — sync guard; useRef supaya klik berganda dalam tick yang sama ditolak. */
  const buyInFlightRef = useRef(false);
  /** R58 — id terbitan dipilih (hero atau related) untuk sambungan selepas auth. */
  const [pendingPurchaseId, setPendingPurchaseId] = useState<string | null>(null);

  // Fasa 4 — borang auth inline: dibuka apabila pembelian memerlukan sesi
  // ('auth-required') atau sesi tamat (status 'expired').
  // §457 F5 — overlay ini log masuk sahaja; pendaftaran berlaku pada `/login`.
  const [authOpen, setAuthOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [buyNotice, setBuyNotice] = useState<string | null>(null);

  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const t = useMemo(() => resolveMarketplaceLabels(profile), [profile]);

  useEffect(
    () => () => {
      if (shareTimer.current !== null) {
        window.clearTimeout(shareTimer.current);
      }
    },
    [],
  );

  // Sesi hub tamat (401 daripada server) — buka borang auth secara automatik
  // supaya pengguna dapat daftar masuk semula tanpa klik beli dua kali.
  useEffect(() => {
    if (hubAuth === 'expired') {
      setAuthError(null);
      setAuthOpen(true);
    }
  }, [hubAuth]);

  const book = useMemo(
    () => books.find((item) => item.publicationId === publicationId) ?? null,
    [books, publicationId],
  );
  const related = useMemo(() => {
    if (book === null) return [];
    // §405 — berkaitan ikut keanggotaan kategori utama ATAU sekunder; subkategori
    // sama didahulukan (peraturan 1: satu utama + sekunder dibenarkan).
    // §466 S1 — buku tanpa kategori di manifest tidak dianggap "berkaitan".
    const candidates = books.filter((item) => {
      if (item.publicationId === book.publicationId) return false;
      if (item.subcategoryId !== null && item.subcategoryId === book.subcategoryId) return true;
      if (book.categoryId === null) return false;
      return (
        item.categoryId === book.categoryId ||
        item.secondaryCategoryIds.includes(book.categoryId) ||
        (item.categoryId !== null && book.secondaryCategoryIds.includes(item.categoryId))
      );
    });
    candidates.sort(
      (a, b) =>
        Number(b.subcategoryId === book.subcategoryId) -
          Number(a.subcategoryId === book.subcategoryId) || a.title.localeCompare(b.title),
    );
    return candidates.slice(0, RELATED_LIMIT);
  }, [books, book]);

  if (book === null) {
    return (
      <div className="hmk">
        <MarketplaceNav
          t={t}
          language={profile.language}
          cartCount={cartCount}
          onNavigate={onNavigate}
        />
        <div className="hmk__detail">
          <div className="hmk__empty hmk__detail-empty">
            <h1 className="hmk__empty-title">{t.detail.notFoundTitle}</h1>
            <p className="hmk__empty-sub">{t.detail.notFoundSub}</p>
            <button
              type="button"
              className="hmk__buy-btn hmk__empty-reset"
              onClick={() => {
                onNavigate('/');
              }}
            >
              {t.detail.backHome}
            </button>
          </div>
        </div>
        <MarketplaceFooter t={t} />
      </div>
    );
  }

  const goHome = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onNavigate('/');
  };

  const clearPendingPurchase = () => {
    setPendingPurchaseId(null);
  };

  const applyPurchaseResult = (targetId: string, result: QxpHubPurchaseResult): void => {
    // §466 S2 / R58 — outcome Hub sahaja; redirect ≠ purchase success.
    if (result === 'granted') {
      clearPendingPurchase();
      onNavigate(`/read/${encodeURIComponent(targetId)}`);
      return;
    }
    if (isQxpHubCheckoutRedirect(result)) {
      clearPendingPurchase();
      window.location.assign(result.checkoutUrl);
      return;
    }
    if (result === 'auth-required') {
      setAuthError(null);
      setAuthOpen(true);
      return;
    }
    clearPendingPurchase();
    setBuyNotice(t.auth.paymentUnavailable);
  };

  const buyPublication = (targetId: string): void => {
    // R58 — hero + related; troli demo BUKAN laluan beli (audit 2026-10-30).
    if (buyInFlightRef.current) return;
    if (!hasCatalogOffer(targetId)) return;
    setBuyNotice(null);
    setPendingPurchaseId(targetId);
    buyInFlightRef.current = true;
    void purchaseViaQxpHub(targetId)
      .then((result) => {
        applyPurchaseResult(targetId, result);
      })
      .finally(() => {
        buyInFlightRef.current = false;
      });
  };

  const onBuyEbook = () => {
    buyPublication(book.publicationId);
  };

  const onSubmitAuth = () => {
    if (authBusy) return;
    setAuthError(null);
    setAuthBusy(true);
    const attempt = loginQxreadHubAccount(authEmail, authPassword);
    void attempt
      .then((result) => {
        if (result.status === 'signed-in') {
          setAuthOpen(false);
          setAuthPassword('');
          // R58 — sambung beli id pending (related), bukan ganti diam-diam ke hero.
          const resumeId = pendingPurchaseId ?? book.publicationId;
          if (buyInFlightRef.current) return undefined;
          buyInFlightRef.current = true;
          return purchaseViaQxpHub(resumeId)
            .then((purchase) => {
              applyPurchaseResult(resumeId, purchase);
            })
            .finally(() => {
              buyInFlightRef.current = false;
            });
        }
        const messages = {
          credentials: t.auth.errorCredentials,
          invalid: t.auth.errorInvalid,
          unavailable: t.auth.errorUnavailable,
        } as const;
        setAuthError(messages[result.error]);
        return undefined;
      })
      .finally(() => {
        setAuthBusy(false);
      });
  };

  const onReadSample = () => {
    onNavigate(`/read/${encodeURIComponent(book.publicationId)}?sample=1`);
  };

  const onShare = () => {
    const url = window.location.href;
    const copyToClipboard = () => {
      if (typeof navigator.clipboard?.writeText !== 'function') {
        return;
      }
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setShared(true);
          if (shareTimer.current !== null) {
            window.clearTimeout(shareTimer.current);
          }
          shareTimer.current = window.setTimeout(() => {
            setShared(false);
          }, SHARE_FEEDBACK_MS);
        })
        .catch(() => undefined);
    };
    if (typeof navigator.share === 'function') {
      navigator.share({ title: book.title, url }).catch(copyToClipboard);
      return;
    }
    copyToClipboard();
  };

  // §466 S1 — tarikh terbit hanya bila manifest menghantar publishedAt sebenar;
  // nilai tidak diketahui tiada baris (bukan tekaan atau tarikh tetap).
  const publishedCell = (() => {
    if (book.publishedAt === null) {
      return null;
    }
    const parsed = new Date(book.publishedAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Intl.DateTimeFormat(safeIntlTag(profile.interfaceTag), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  })();

  const authorProfilePath =
    book.author === null ? null : `/author/${encodeURIComponent(slugifyAuthorName(book.author))}`;
  const categoryCell = (() => {
    if (book.categoryId === null) {
      return book.subcategoryId === null ? null : qxCategoryName(book.subcategoryId, t.language);
    }
    const main = qxCategoryName(book.categoryId, t.language);
    return book.subcategoryId === null
      ? main
      : `${main} › ${qxCategoryName(book.subcategoryId, t.language)}`;
  })();

  return (
    <div className="hmk">
      <MarketplaceNav
        t={t}
        language={profile.language}
        cartCount={cartCount}
        onNavigate={onNavigate}
      />
      <div className="hmk__detail">
        <p className="hmk__detail-back">
          <a href="/" onClick={goHome}>
            <LandingIcon name="chevronLeft" size={13} /> {t.detail.back}
          </a>
        </p>

        {/* 1 · HERO PRODUCT */}
        <MarketplaceDetailHero
          t={t}
          book={book}
          shared={shared}
          purchasable={hasCatalogOffer(book.publicationId)}
          onBuy={onBuyEbook}
          onReadSample={onReadSample}
          onShare={onShare}
          onNavigate={onNavigate}
        />

        {buyNotice === null ? null : (
          <p className="hmk__section-text" role="status">
            {buyNotice}
          </p>
        )}

        {/* 2 · ABOUT THIS EBOOK */}
        <section className="hmk__section" aria-labelledby="hmk-detail-about">
          <h2 id="hmk-detail-about" className="hmk__section-title">
            {t.detail.aboutTitle}
          </h2>
          <p className="hmk__section-text">{book.description ?? t.detail.descriptionFallback}</p>
        </section>

        {/* 3 · EBOOK DETAILS — hanya medan yang dihantar manifest hub */}
        <section className="hmk__section" aria-labelledby="hmk-detail-facts">
          <h2 id="hmk-detail-facts" className="hmk__section-title">
            {t.detail.detailsTitle}
          </h2>
          <table className="hmk__facts">
            <tbody>
              {book.author === null ? null : (
                <tr>
                  <th scope="row">{t.detail.detailLabels.author}</th>
                  <td>{book.author}</td>
                </tr>
              )}
              {book.languageRef === null ? null : (
                <tr>
                  <th scope="row">{t.detail.detailLabels.language}</th>
                  <td>{languageDisplayLabel(book.languageRef, t.language)}</td>
                </tr>
              )}
              {categoryCell === null ? null : (
                <tr>
                  <th scope="row">{t.detail.detailLabels.category}</th>
                  <td>{categoryCell}</td>
                </tr>
              )}
              {publishedCell === null ? null : (
                <tr>
                  <th scope="row">{t.detail.detailLabels.published}</th>
                  <td>{publishedCell}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* 4 · ABOUT THE AUTHOR — §466 S1: identiti sahaja (manifest hantar nama);
            tiada bio rekaan sehingga hub menghantar profil penulis sebenar. */}
        {authorProfilePath === null ? null : (
          <section className="hmk__section" aria-labelledby="hmk-detail-author">
            <h2 id="hmk-detail-author" className="hmk__section-title">
              {t.detail.authorTitle}
            </h2>
            <p className="hmk__section-text">{book.author}</p>
            <p className="hmk__section-link">
              <a
                href={authorProfilePath}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(authorProfilePath);
                }}
              >
                {t.detail.authorProfileLink} →
              </a>
            </p>
          </section>
        )}

        {/* 5 · REVIEWS (jujur — real data only) & RELATED */}
        <section className="hmk__section" aria-labelledby="hmk-detail-reviews">
          <h2 id="hmk-detail-reviews" className="hmk__section-title">
            {t.detail.reviewsTitle}
          </h2>
          <p className="hmk__section-text hmk__reviews-empty">{t.detail.reviewsEmpty}</p>
        </section>

        {related.length > 0 ? (
          <section className="hmk__detail-related" aria-labelledby="hmk-detail-related-title">
            <h2 id="hmk-detail-related-title" className="hmk__detail-related-title">
              {t.detail.moreLikeThis}
            </h2>
            <ul className="hmk__grid" aria-label={t.detail.moreLikeThis}>
              {related.map((item) => (
                <BookCard
                  key={item.publicationId}
                  t={t}
                  book={item}
                  added={false}
                  purchasable={hasCatalogOffer(item.publicationId)}
                  onNavigate={onNavigate}
                  onBuy={(target) => {
                    buyPublication(target.publicationId);
                  }}
                />
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      <MarketplaceFooter t={t} />

      {authOpen ? (
        <MarketplaceDetailAuth
          t={t}
          authEmail={authEmail}
          authPassword={authPassword}
          authError={authError}
          authBusy={authBusy}
          onEmailChange={setAuthEmail}
          onPasswordChange={setAuthPassword}
          onClose={() => {
            setAuthOpen(false);
            clearPendingPurchase();
          }}
          onSubmit={onSubmitAuth}
          onCreateAccount={() => {
            setAuthOpen(false);
            clearPendingPurchase();
            onNavigate(
              `/login?returnTo=${encodeURIComponent(`/publication/${book.publicationId}`)}`,
            );
          }}
        />
      ) : null}
    </div>
  );
}
